'use client';

import Button from '@/components/ui/Button';
import Menu from '@/components/ui/Menu';
import MenuItem from '@/components/ui/MenuItem';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { FrontUserBalanceResponseDto, FrontUserBalanceService } from '@/lib/jp-api/requests';
import { isAuthenticated } from '@/lib/helper/is-authenticated';
import { CmsParams } from '@/lib/cms-params.type';
import { formatCurrency } from '@/lib/currency-formatter';
import { noCurrencySelected } from '@/lib/currency/currency-persistence';
import { CurrencyContext } from '@/components/currency-provider/currency-provider';
import { useTranslations } from '@/lib/hooks/useTranslations';
import { IconsAvailable } from '@/components/ui/Icon';
import { currencyIconList } from '@/components/currency-selector/currency-icon-list';

type CurrencySelectorProps = {
  cmsParams: CmsParams;
  ns?: string;
  className?: string;
};

function formatBalanceRow(balance?: FrontUserBalanceResponseDto) {
  if (!balance) return null;
  return `${balance.label} (${formatCurrency(balance.currencyCode, balance.balance)})`;
}

function getIcon(balance: FrontUserBalanceResponseDto) {
  if (!balance)
    return {
      iconLeft: 'money-circle' as IconsAvailable,
    };

  if (balance.icon)
    return {
      imageLeft: `/images/custom-currency-icons/${balance.icon}`,
    };

  if (currencyIconList.includes(balance.currencyCode.toLowerCase())) {
    return {
      imageLeft: `/images/cryptocurrency-icons/${balance.currencyCode.toLowerCase()}.svg`,
    };
  }

  return {
    iconLeft: 'money-circle' as IconsAvailable,
  };
}

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({ cmsParams, className, ns }) => {
  const t = useTranslations(`CurrencySelector`, ns);
  const [userBalances, setUserBalances] = useState<FrontUserBalanceResponseDto[]>([]);
  const isUserAuthenticated = isAuthenticated(cmsParams);
  const { currency, setCurrency } = useContext(CurrencyContext);

  useEffect(() => {
    if (!isUserAuthenticated) return;
    FrontUserBalanceService.getFrontUserBalanceBalance().then((data) => {
      setUserBalances(data);
      if (noCurrencySelected(currency) && data.length !== 0) {
        const firstCurrency = data[0];
        const currencyWithFunds = data.find((b) => b.balance > 0);
        setCurrency(currencyWithFunds ? currencyWithFunds.currencyCode : firstCurrency.currencyCode);
      }
    });
    // eslint-disable-next-line
  }, [cmsParams, isUserAuthenticated]);

  if (!userBalances || userBalances.length === 0) return null;
  const selectedCurrency = userBalances.find((b) => b.currencyCode === currency);
  if (!selectedCurrency) return null;

  return (
    <Button
      className={className}
      title={formatBalanceRow(selectedCurrency)}
      {...getIcon(selectedCurrency)}
      size="small"
      menu={
        <Menu
          footer={
            <Button
              size="small"
              theme="primary"
              iconRight="plus-filled"
              title={t('btnDeposit')}
              onClick={() => {
                window.open('https://www.luckyblock.com/en?overlay=wallet&tab=0');
              }}
            />
          }
        >
          {userBalances.map((balance) => (
            <MenuItem
              key={balance.currencyCode}
              title={formatBalanceRow(balance)}
              {...getIcon(balance)}
              active={currency === balance.currencyCode}
              onClick={() => setCurrency(balance.currencyCode)}
            />
          ))}
        </Menu>
      }
    />
  );
};
